import React from 'react';
import AppButton from '../../../../../components/common/AppButton';
import {SVG} from '../../../../../constants/assetLists';
import {useFieldArray, useFormContext} from 'react-hook-form';
import {
  createDefaultNetwork,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
  SelectedTreeNode,
} from "../types";
import NetworkTree from "./tree/NetworkTree";
import DetailsForm from "./form/DetailsForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import {
  handleActivateOnBoardingDraftService,
  handleUpsertOnBoardingDraftService,
} from "../../../../../services/on-boarding";
import { buildOnBoardingDraftPayload } from "../../../buildOnBoardingDraftPayload";
import { filterInvalidNetworkTree } from "../networkDetailsSchema";
import { useNetworkDetailsDraft } from "../NetworkDetailsDraftContext";
import QUERY_KEYS from "../../../../../constants/queryKeys";
import FullScreenLoader from "../../../../../components/common/FullScreenLoader";
import { useSnackbar } from "../../../../../context/SnackbarContextProvider";
import AppConfirmationDialog from "../../../../../components/common/AppConfirmationDialog";
import { isSuperAdmin, isSuperAdminOnly } from "../../../../../utils";

type Props = {
  selectedFieldPath: NetworkNodeFieldPath | null;
  selectedNode: SelectedTreeNode | null;
  setSelectedNode: React.Dispatch<
    React.SetStateAction<SelectedTreeNode | null>
  >;
  isHydrated: boolean;
};

function NetworkParent({
  selectedNode,
  setSelectedNode,
  selectedFieldPath,
  isHydrated,
}: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { clearSavedData, markDataSaved } = useNetworkDetailsDraft();
  const [showDraftSuccessDialog, setShowDraftSuccessDialog] =
    React.useState(false);
  const [showActiveConfirmationDialog, setShowActiveConfirmationDialog] =
    React.useState(false);
  const {
    control,
    getValues,
    setValue,
    trigger,
    formState: {isDirty},
  } = useFormContext<NetworkDetailsFormValues>();
  const { append: networkAppend, remove: removeNetwork } = useFieldArray({
    control: control,
    name: "network",
    keyName: "id",
  });

  const { mutateAsync: saveDraft, isPending: isSaving } = useMutation({
    mutationFn: handleUpsertOnBoardingDraftService,
  });

  const { mutate: activateDraft, isPending: isActivating } = useMutation({
    mutationFn: handleActivateOnBoardingDraftService,
    onSuccess: async () => {
      // Only the network owner's own stored user carries the onboarding flag;
      // a super admin activates on behalf of the owner.
      const storedUser = localStorage.getItem("user");
      const user = storedUser ? JSON.parse(storedUser) : null;
      if (user && !isSuperAdminOnly()) {
        user.completedOnboarding = true;
        localStorage.setItem("user", JSON.stringify(user));
      }
      await clearSavedData();
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ON_BOARDING_DRAFT],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ADMIN_ON_BOARDING_DRAFTS],
      });
      navigate(-1);
    },
  });

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSave = async () => {
    if (isSaving || isActivating) return;

    try {
      const cleanedNetwork = filterInvalidNetworkTree(getValues("network"));
      setValue("network", cleanedNetwork, { shouldDirty: true });
      const isValidData = await trigger();
      const transformedNetwork =
        await buildOnBoardingDraftPayload(cleanedNetwork);

      await saveDraft({ data: transformedNetwork, isValidData });
      await markDataSaved(transformedNetwork);

      if (!isSuperAdminOnly()) {
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ON_BOARDING_DRAFT],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ADMIN_ON_BOARDING_DRAFTS],
        });
      }

      if (isValidData) {
        setShowActiveConfirmationDialog(true);
      } else {
        setShowDraftSuccessDialog(true);
      }
    } catch (error) {
      console.error("On-boarding draft save failed:", error);
      showSnackbar({
        message: "שגיאה בשמירת הנתונים. נסו שוב.",
        type: "error",
      });
    }
  };

  const addNetworkNode = () => {
    networkAppend(createDefaultNetwork());

    setSelectedNode({
      level: 'network',
      networkIndex: getValues('network').length - 1
    });
  };

  return (
    <>
      <div className="w-full h-full py-4 flex flex-col">
        {(isSaving || isActivating) && <FullScreenLoader open />}

        <div className="w-full flex-1 min-h-0 px-[32px] flex flex-col gap-3">
          <div className="flex flex-col gap-2 pb-1 border-b border-b-gray80">
            <p className="m-0 text-xl text-deepAqua font-bold">עץ הארגון</p>

            <p className="m-0 text-base text-darkerGray">
              הגדירו את מבנה הארגון. תוכלו תמיד לחזור ולהוסיף או להחסיר ענפים.
            </p>
          </div>

          {isSuperAdmin() && <div>
            <AppButton
              variant="primary"
              className="w-[376px] h-[40px] justify-center"
              onClick={addNetworkNode}
              iconStart={
                <img
                  src={SVG.addWhiteIcon}
                  alt="icon"
                  className="w-3 h-3 object-contain"
                />
              }
            >
              הוספת רשת/ארגון אב
            </AppButton>
          </div>}

          <div className="flex-1 flex gap-2 min-h-0 overflow-y-hidden">
            <NetworkTree
              removeNetwork={removeNetwork}
              selectedNode={selectedNode}
              onSelectNode={setSelectedNode}
              isHydrated={isHydrated}
            />

            <DetailsForm selectedFieldPath={selectedFieldPath} />
          </div>
        </div>

        <div className="w-full mt-3 px-6 pt-2.5 flex justify-end items-center gap-3 border-t border-t-gray80">
          <AppButton onClick={handleCancel} disabled={isSaving || isActivating}>
            ביטול
          </AppButton>

          <AppButton
            variant="primary"
            onClick={() => void handleSave()}
            disabled={isSaving || isActivating || !isDirty}
          >
             שמירה ויציאה
          </AppButton>
        </div>
      </div>

      <AppConfirmationDialog
        open={showDraftSuccessDialog}
        onClose={() => setShowDraftSuccessDialog(false)}
        icon={
          <img
            src={SVG.infoBlueIcon}
            alt="icon"
            className="w-[46px] h-[46px] object-contain"
          />
        }
        bordered={false}
        containerClasses="!px-2"
        title="המערכת עדיין לא מוכנה"
        subTitle={
          <div className="flex flex-col gap-2">
            <p className="m-0 text-base text-darkGray">התוכן שהעלתם ישמר.</p>
            <p className="m-0 text-base text-darkGray">
              השלימו את שדות החובה כדי להפעיל
            </p>
            <p className="m-0 text-base text-darkGray">תוכלו להשלים בכל עת.</p>
          </div>
        }
        footer={
          <div className="flex items-center justify-end gap-2 mt-4">
            <AppButton onClick={() => setShowDraftSuccessDialog(false)}>
              ביטול
            </AppButton>
            <AppButton
              variant="primary"
              onClick={() => navigate(-1)}
            >
              שמירה
            </AppButton>
          </div>
        }
      />

      <AppConfirmationDialog
        open={showActiveConfirmationDialog}
        onClose={() => setShowActiveConfirmationDialog(false)}
        icon={
          <img
            src={SVG.activateCheckIcon}
            alt="icon"
            className="w-[56px] h-[56px] object-contain"
          />
        }
        width={484}
        bordered={false}
        containerClasses="!px-2"
        title="ברכות, המערכת מוכנה"
        subTitle={
          <div className="flex flex-col gap-2 max-w-[264px]">
            <p className="m-0 text-base text-center text-darkGray">
              אתם יכולים להתחיל.
            </p>
            <p className="m-0 text-base text-center text-darkGray">
              בלחיצה על &quot;הפעלת המערכת&quot; ישלחו לכל המשתמשים שהגדרתם
              לינקים להורדת האפליקציה.
            </p>
            <p className="m-0 text-base text-center text-darkGray">
              תוכלו להפעיל בכל שלב שתבחרו.
            </p>
          </div>
        }
        footer={
          <div className="flex items-center justify-end gap-2 mt-4">
            <AppButton onClick={() => setShowActiveConfirmationDialog(false)}>
              ביטול
            </AppButton>
            <AppButton onClick={() => navigate(-1)}>
              יציאה ללא הפעלה
            </AppButton>
            <AppButton variant="primary" onClick={() => activateDraft()}>
              הפעלת המערכת
            </AppButton>
          </div>
        }
      />
    </>
  );
}

export default NetworkParent;
